import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { useSelectionCountContext } from 'core/states';
import { IInfiniteQueryOptions } from 'core/types';
import { partBomItemService } from 'partBoms/services';
import { IPartBomItem, IPartBomItemListRequestPayload } from 'partBoms/types';

export const useGetPartBomItems = (
  requestPayload?: IPartBomItemListRequestPayload,
  options: IInfiniteQueryOptions<IPartBomItem> = {}
) =>
  useInfiniteQuery<IPartBomItem>(
    partBomItemService.getPartBomItems,
    'partBomItems',
    requestPayload,
    options
  );

export const usePartBomItemActions = (notificationPosition?: NotificationPosition) => {
  const { selectionCount } = useSelectionCountContext();

  return {
    ...useUpsertPartBomItem(notificationPosition),
    ...useValidatePart(),
    ...useRemovePartBomItems(selectionCount),
  };
};

const useUpsertPartBomItem = (notificationPosition = NotificationPosition.FORM) => {
  const { errors } = useHandleErrors({
    success: ({ partBomItem }) =>
      partBomItem.id
        ? { message: t('messagePartBomItemUpdateSuccess') }
        : { message: t('messagePartBomItemCreateSuccess') },
    loading: ({ partBomItem }) =>
      partBomItem.id
        ? { message: t('messagePartBomItemUpdateInProgress') }
        : { message: t('messagePartBomItemCreateInProgress') },
    error: ({ partBomItem }) =>
      partBomItem.id
        ? { title: t('errorTitlePartBomItemUpdate') }
        : { title: t('errorTitlePartBomItemCreate') },
    invalidateQueries: ['partBomDetails'],
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(partBomItemService.upsertPartBomItem, {
    ...errors,
  });

  return { upsertPartBomItem: mutateAsync };
};

const useValidatePart = () => {
  const { errors } = useHandleErrors({
    loading: () => ({ message: t('messagePartBomItemValidateInProgress') }),
    error: () => ({ title: t('errorTitlePartBomItemValidate') }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(partBomItemService.validatePart, {
    ...errors,
  });

  return { validatePart: mutateAsync };
};

const useRemovePartBomItems = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    loading: {
      message: t('messagePartBomItemsRemoveInProgress', { count: selectionCount }),
    },
    error: {
      title: t('errorTitlePartBomItemsRemove', { count: selectionCount }),
    },
    invalidateQueries: ['partBomItems', 'partBomDetails'],
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(partBomItemService.removePartBomItems, {
    ...errors,
  });

  return { removePartBomItems: mutateAsync };
};
